import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import GradientOutlined from '@mui/icons-material/GradientOutlined';
import PageHeader from '../components/common/PageHeader';
import {
  getFabricGrades,
  createFabricGrade,
  updateFabricGrade,
  deleteFabricGrade,
} from '../services/fabricGradesApi';

const FabricGrades = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await getFabricGrades();
      setGrades(data.items);
    } catch (err) {
      console.error('Failed to fetch fabric grades:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await createFabricGrade(newName.trim());
      setDialogOpen(false);
      setNewName('');
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grade? This will remove all associated prices and unlink fabric patterns.')) return;
    setDeleting(id);
    try {
      await deleteFabricGrade(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  const startEdit = (grade) => {
    setEditingId(grade.id);
    setEditName(grade.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    setSavingEdit(true);
    try {
      await updateFabricGrade(editingId, { name: editName.trim() });
      setEditingId(null);
      await fetchData();
    } catch (err) {
      console.error('Failed to update grade:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Fabric Grades" subtitle="Manage fabric grade definitions" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Fabric Grades"
        subtitle="Manage fabric grade definitions"
        action={
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => setDialogOpen(true)}>
            Add Grade
          </Button>
        }
      />

      {grades.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <GradientOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No fabric grades added
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Grade" to create your first grade
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Grade Name
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', width: 120 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {grades.map((grade) => (
                  <TableRow key={grade.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      {editingId === grade.id ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                            autoFocus
                            sx={{ width: 200 }}
                          />
                          <IconButton size="small" onClick={handleSaveEdit} disabled={savingEdit} sx={{ color: 'primary.main' }}>
                            {savingEdit ? <CircularProgress size={16} /> : <SaveRounded fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={cancelEdit}>
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {grade.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editingId !== grade.id && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => startEdit(grade)} sx={{ color: 'primary.main' }}>
                              <EditRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(grade.id)}
                              disabled={deleting === grade.id}
                              sx={{ color: 'error.main' }}
                            >
                              {deleting === grade.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <DeleteOutlineRounded fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add Grade Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setError(''); setNewName(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add Fabric Grade</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Grade Name"
            placeholder="e.g. A, B, C, Premium"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            fullWidth
            sx={{ mt: 1 }}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); setError(''); setNewName(''); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FabricGrades;
